import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { fetchApplications, deleteApplication, fetchApplicationById } from "../../../services/check-api";
import Table from "../../../utils/Table";
import ActionButtons from "../../../utils/ActionButtons";
import { getStatusDot, formatDate } from "../../../utils/TableUtils";
import CheckApplicationModal from "../../../components/Modals/CheckApplicationModal";
import CommentsModal from "../../../components/Modals/CommentsModal";
import FilesModal from "../../../components/Modals/FilesModal";
import { useAuth } from "../../../contexts/AuthContext"; 
import { useToast } from "../../../contexts/ToastContext";
import { useLoading } from "../../../contexts/LoadingContext";


function Applications({ isTab = false, onCountChange }) {
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [filterInput, setFilterInput] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [commentsModalShow, setCommentsModalShow] = useState(false);
  const [selectedComments, setSelectedComments] = useState("");
  const [filesModalShow, setFilesModalShow] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { role } = useAuth();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const pageSize = 10;

  const handleEdit = (applicationData) => {
    setSelectedApplication(applicationData);
    setModalShow(true);
  };

  const refreshAppData = () => {
    setModalShow(false);
    setSelectedApplication(null);
    if (id) {
      fetchData(id);
    } else {
      fetchData();
    }
    
  }

  const handleDelete = async (rowData) => {
    startLoading();
    try {
      const { id: appId } = rowData;
      if (!appId) {
        showToast("Invalid ID, please try again.", "error");
        return;
      }
      const response = await deleteApplication(appId);

      if (response && response.message) {
        setData((prevData) =>
          prevData.filter((applicaton) => applicaton.id !== appId)
        );
        showToast(response.message, "success");
      }
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("Error deleting application", "error");
      }
      console.error("Error deleting application:", error);
    } finally {
      stopLoading();
    }
  };

  const handlePageChange = (newPageIndex) => {
    setPageIndex(newPageIndex);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Role",
        accessor: "role",
      },
      {
        Header: "Name",
        accessor: "name",
        minWidth: 150,
        sortType: (rowA, rowB) => {
          const a = rowA.values.name?.toLowerCase() || "";
          const b = rowB.values.name?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Address",
        accessor: "address",
        minWidth: 400,
        Cell: ({ value }) => (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value}
          </div>
        ),
        sortType: (rowA, rowB) => {
          const a = rowA.values.address?.toLowerCase() || "";
          const b = rowB.values.address?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Phone Number",
        accessor: "phoneNumber",
        sortType: (rowA, rowB) => {
          const a = rowA.values.phoneNumber?.toLowerCase() || "";
          const b = rowB.values.phoneNumber?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Email",
        accessor: "email",
        sortType: (rowA, rowB) => {
          const a = rowA.values.email?.toLowerCase() || "";
          const b = rowB.values.email?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Community Email Address",
        accessor: "communityEmailAddress",
        sortType: (rowA, rowB) => {
          const a = rowA.values.communityEmailAddress?.toLowerCase() || "";
          const b = rowB.values.communityEmailAddress?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Plan Review Type",
        accessor: "planReviewType",
        sortType: (rowA, rowB) => {
          const a = rowA.values.planReviewType?.toLowerCase() || "";
          const b = rowB.values.planReviewType?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Comments",
        accessor: "comments",
        Cell: ({ row }) => {
          const comments = row.original.comments;
          if (!comments) {
            return <span style={{ color: '#999' }}>No comments</span>;
          }
          return (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedComments(comments);
                setCommentsModalShow(true);
              }}
              style={{ 
                color: '#3d95b9', 
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              View Comments
            </a>
          );
        },
        sortType: (rowA, rowB) => {
          const a = rowA.values.comments?.toLowerCase() || "";
          const b = rowB.values.comments?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      {
        Header: "Files",
        accessor: "fileLinks",
        Cell: ({ row }) => {
          const fileLinks = row.original.fileLinks;
          if (!fileLinks || fileLinks.length === 0) {
            return <span style={{ color: '#999' }}>No files</span>;
          }
          return (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedFiles(fileLinks);
                setFilesModalShow(true);
              }}
              style={{ 
                color: '#3d95b9', 
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              View Files ({fileLinks.length})
            </a>
          );
        },
        disableSortBy: true,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => getStatusDot(value),
        sortType: (rowA, rowB) => {
          const a = rowA.values.status?.toLowerCase() || "";
          const b = rowB.values.status?.toLowerCase() || "";
          return a.localeCompare(b);
        }
      },
      
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <ActionButtons
            onEdit={handleEdit} 
            onDelete={handleDelete}
            rowData={row.original}
            showDelete={role === "Admin"}
            additonalBtn={false} 
          />
        ),
      },
    ];
  
    return baseColumns;
  }, [role]);
  
  const fetchData = async (id = null, page = null, filter = null) => {
    setLoading(true);
    try {
      const currentPage = page !== null ? page : pageIndex;
      const currentFilter = filter !== null ? filter : filterInput;
      let response;
      if (id) {
        // Fetch data for a specific application by ID
        response = await fetchApplicationById(id, currentPage + 1, pageSize, null, currentFilter);
        setData(response.data);
        if (onCountChange && response.pagination) onCountChange(response.pagination.totalCount);
      } else {
        // Fetch all applications with filter
        response = await fetchApplications(currentPage + 1, pageSize, null, currentFilter);
        setData(response.data);
        setPageCount(response.pagination.totalPages);
        if (onCountChange && response.pagination) onCountChange(response.pagination.totalCount);
      }
    } catch (error) {
      console.error("Error fetching data:", error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 0 when filter changes and fetch data
  useEffect(() => {
    setPageIndex(0);
    if (id) {
      fetchData(id, 0, filterInput);
    } else {
      fetchData(null, 0, filterInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterInput, id]);

  useEffect(() => {
    if (id) {
      fetchData(id);
    } else {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  return (
    <div className={isTab ? "" : "subMain-content"}>
      {!isTab && (
        <div>
          <h1 className="mt-4">Plan Review Request</h1>
        </div>
      )}
    <Table
      className="custom-simplecheck-table"
      columns={columns}
      data={data}
      filterInput={filterInput}
      setFilterInput={setFilterInput}
      onPageChange={handlePageChange}
      currentPage={pageIndex}
      pageCount={pageCount}
      loading={loading}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
    />
    {selectedApplication && <CheckApplicationModal
        communityId={id}
        show={modalShow}
        data={selectedApplication}
        onHide={refreshAppData}
      />}
    <CommentsModal
      show={commentsModalShow}
      onHide={() => setCommentsModalShow(false)}
      comments={selectedComments}
    />
    <FilesModal
      show={filesModalShow}
      onHide={() => setFilesModalShow(false)}
      files={selectedFiles}
    />
    </div>
  );
}

export default Applications;
